import { useState, useEffect, useCallback, useRef } from 'react'
import { Subscription } from 'rxjs'
import { get } from 'utils/api-routes.util'

export interface InventoryResponse {
  data: any[]
  meta: {
    total: number
    period: {
      start_date: string
      end_date: string
    }
    sort_by: string
    filters: {
      include_deleted?: boolean
      excluded_statuses?: string[]
      include_archived?: boolean
      [key: string]: boolean | string[] | undefined
    }
  }
}

export interface InventoryFilters {
  [key: string]: any
}

export type InventoryReportType =
  | 'most-count'
  | 'least-count'
  | 'run-rate'
  | 'selling-faster'
  | 'selling-slower'
  | 'dead-stock'
  | 'stock-turnover'

interface UseInventoryDataReturn {
  data: InventoryResponse | null
  loading: boolean
  error: string | null
  refetch: () => void
}

/**
 * Generic hook for fetching inventory data
 */
export const useInventoryData = (
  reportType: InventoryReportType,
  filters: InventoryFilters = {},
  autoFetch: boolean = true,
): UseInventoryDataReturn => {
  const [data, setData] = useState<InventoryResponse | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const subscriptionRef = useRef<Subscription | null>(null)

  const fetchData = useCallback(() => {
    setLoading(true)
    setError(null)

    // Cancel previous subscription if exists
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe()
    }

    const url = `/inventory-dashboard/${reportType}`

    subscriptionRef.current = get<InventoryResponse>(url, filters).subscribe({
      next: (res: {
        response?: InventoryResponse
        data?: InventoryResponse
      }) => {
        // Handle AjaxResponse format - same pattern as TopCitiesChart
        let responseData: InventoryResponse | null = null
        if (res && res.response) {
          responseData = res.response
        } else if (res && res.data) {
          responseData = res.data
        }

        if (responseData) {
          setData(responseData)
        } else {
          setData(null)
        }
        setLoading(false)
      },
      error: (err) => {
        console.error(`Error fetching ${reportType} data:`, err)
        setError(err?.message || `Failed to fetch ${reportType} data`)
        setLoading(false)
      },
    })
  }, [reportType, filters])

  const refetch = useCallback(() => {
    fetchData()
  }, [fetchData])

  // Auto-fetch on mount and when dependencies change
  useEffect(() => {
    if (autoFetch) {
      fetchData()
    }

    // Cleanup subscription on unmount
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
      }
    }
  }, [autoFetch, fetchData])

  return { data, loading, error, refetch }
}

/**
 * Hook for most count data
 */
export const useMostCountData = (filters: InventoryFilters = {}) => {
  return useInventoryData('most-count', filters)
}

/**
 * Hook for least count data
 */
export const useLeastCountData = (filters: InventoryFilters = {}) => {
  return useInventoryData('least-count', filters)
}

/**
 * Hook for run rate data
 */
export const useRunRateData = (filters: InventoryFilters = {}) => {
  return useInventoryData('run-rate', filters)
}

/**
 * Hook for selling faster data
 */
export const useSellingFasterData = (filters: InventoryFilters = {}) => {
  return useInventoryData('selling-faster', filters)
}

/**
 * Hook for selling slower data
 */
export const useSellingSlowerData = (filters: InventoryFilters = {}) => {
  return useInventoryData('selling-slower', filters)
}

/**
 * Hook for dead stock data
 */
export const useDeadStockData = (filters: InventoryFilters = {}) => {
  return useInventoryData('dead-stock', filters)
}

/**
 * Hook for stock turnover data
 */
export const useStockTurnoverData = (filters: InventoryFilters = {}) => {
  return useInventoryData('stock-turnover', filters)
}
